import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Pages anyone may open, signed in or not: an old session cookie must not bounce visitors to the login page.
const PUBLIC_PREFIXES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify', '/courses', '/about'];
const isPublicPath = (path) => path === '/' || PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshing = useRef(null);

  // Session rules: an expired access token is refreshed once and the request retried; a session that was
  // ended because the account signed in on another device (or by an admin) sends the user to the login page.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        const original = error.config || {};
        const isAuthCall = (original.url || '').includes('/api/auth/');
        // The login cookie lasts 15 minutes: afterwards the browser drops it ("Not authenticated") or the server says
        // "Token expired". Either way, renew it silently with the long-lived refresh cookie and repeat the request,
        // so a student who spent 20 minutes on a quiz can still submit it.
        const expired = detail === 'Token expired' || detail === 'Not authenticated';
        if (status === 401 && expired && !original._retried && !isAuthCall) {
          original._retried = true;
          try {
            refreshing.current = refreshing.current || axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
            await refreshing.current;
            refreshing.current = null;
            return axios(original);
          } catch (refreshError) {
            refreshing.current = null;
            // the refresh cookie is gone too (7 days) or the session was ended: ask the user to sign in again
            if (!isPublicPath(window.location.pathname)) {
              setUser(false);
              window.location.href = '/login?reason=ended';
            }
          }
        }
        if (status === 401 && typeof detail === 'string' && (detail.startsWith('You were signed out') || detail.startsWith('Your session has ended'))) {
          setUser(false);
          if (!isPublicPath(window.location.pathname)) {
            const reason = detail.startsWith('You were signed out') ? 'other_device' : 'ended';
            window.location.href = `/login?reason=${reason}`;
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const register = async (name, email, password, role = 'student') => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { name, email, password, role },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (error) {
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
